import { useState, useEffect } from "react";
import { useGoogleAuth } from "@/hooks/useGoogleAuth";
import { trpc } from "../lib/trpc";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Plus, Download, Upload, Edit2, Trash2 } from "lucide-react";
import { useLocation } from "wouter";
import * as XLSX from "xlsx";
import {
  parseDecimal,
  parseIntPositive,
  parseIntOrNull,
  parseNumericString,
  validateRows,
  formatValidationErrors,
  type ValidationError,
} from "../lib/fiscalImportUtils";

export default function PIS_COFINS() {
  const { user, loading: authLoading } = useGoogleAuth();
  const [, setLocation] = useLocation();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isBulkDialogOpen, setIsBulkDialogOpen] = useState(false);
  const [isUpdateBulkDialogOpen, setIsUpdateBulkDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isNatureModalOpen, setIsNatureModalOpen] = useState(false);
  const [natureSearchTerm, setNatureSearchTerm] = useState("");
  const [isEmitenteModalOpen, setIsEmitenteModalOpen] = useState(false);
  const [isBulkNatureModalOpen, setIsBulkNatureModalOpen] = useState(false);
  const [bulkNatureSearchTerm, setBulkNatureSearchTerm] = useState("");

  const [formData, setFormData] = useState({
    ncm: "",
    origem: 0,
    aliquotaPis: 0,
    aliquotaCofins: 0,
    cstPis: "",
    cstCofins: "",
    naturezaOperacaoId: undefined as number | undefined,
    naturezaOperacaoNome: "",
    gatilho: "" as string,
    emitenteId: undefined as number | undefined,
    emitenteNome: "",
  });

  const [bulkUpdateData, setBulkUpdateData] = useState({
    aliquotaPis: undefined as number | undefined,
    aliquotaCofins: undefined as number | undefined,
    cstPis: undefined as string | undefined,
    cstCofins: undefined as string | undefined,
    naturezaOperacaoId: undefined as number | undefined,
    gatilho: undefined as string | undefined,
  });

  // Auth redirect
  useEffect(() => {
    if (!authLoading && !user) {
      window.location.href = "/login";
    }
  }, [user, authLoading]);

  // Queries
  const { data: pisCofinsList = [], refetch } = trpc.pisCofins.list.useQuery();
  const { data: operationNatures = [] } = trpc.operationNatures.list.useQuery();
  const { data: emitentes = [] } = trpc.emitentes.list.useQuery();

  // Mutations
  const createMutation = trpc.pisCofins.create.useMutation({
    onSuccess: () => {
      toast.success("PIS/COFINS criado com sucesso!");
      setIsDialogOpen(false);
      setEditingId(null);
      resetForm();
      refetch();
    },
    onError: (error) => {
      toast.error(error.message || "Erro ao criar PIS/COFINS");
    },
  });

  const createBulkMutation = trpc.pisCofins.createBulk.useMutation({
    onSuccess: (data) => {
      toast.success(`${data.count} registros importados com sucesso!`);
      setIsBulkDialogOpen(false);
      refetch();
    },
    onError: (error) => {
      toast.error(error.message || "Erro ao importar registros");
    },
  });

  const updateMutation = trpc.pisCofins.update.useMutation({
    onSuccess: () => {
      toast.success("PIS/COFINS atualizado com sucesso!");
      setIsDialogOpen(false);
      setEditingId(null);
      resetForm();
      refetch();
    },
    onError: (error) => {
      toast.error(error.message || "Erro ao atualizar PIS/COFINS");
    },
  });

  const updateBulkMutation = trpc.pisCofins.updateBulk.useMutation({
    onSuccess: (data) => {
      toast.success(`${data.count} registros atualizados com sucesso!`);
      setIsUpdateBulkDialogOpen(false);
      setSelectedIds([]);
      refetch();
    },
    onError: (error) => {
      toast.error(error.message || "Erro ao atualizar registros");
    },
  });

  const deleteMutation = trpc.pisCofins.delete.useMutation({
    onSuccess: (data) => {
      toast.success(`${data.count} registro(s) excluído(s) com sucesso!`);
      setSelectedIds([]);
      refetch();
    },
    onError: (error) => {
      toast.error(error.message || "Erro ao excluir registros");
    },
  });

  const resetForm = () => {
    setFormData({
      ncm: "",
      origem: 0,
      aliquotaPis: 0,
      aliquotaCofins: 0,
      cstPis: "",
      cstCofins: "",
      naturezaOperacaoId: undefined,
      naturezaOperacaoNome: "",
      gatilho: "",
      emitenteId: undefined,
      emitenteNome: "",
    });
  };

  const handleOpenDialog = (item?: any) => {
    if (item) {
      setEditingId(item.id);
      setFormData({
        ncm: item.ncm,
        origem: parseInt(item.origem),
        aliquotaPis: parseFloat(item.aliquotaPis),
        aliquotaCofins: parseFloat(item.aliquotaCofins),
        cstPis: item.cstPis,
        cstCofins: item.cstCofins,
        naturezaOperacaoId: item.naturezaOperacaoId || undefined,
        naturezaOperacaoNome: item.naturezaOperacaoNome || "",
        gatilho: item.gatilho || "",
        emitenteId: item.emitenteId || undefined,
        emitenteNome: item.emitenteNome || "",
      });
    } else {
      setEditingId(null);
      resetForm();
    }
    setIsDialogOpen(true);
  };

  const handleSubmit = () => {
    if (!formData.ncm || !formData.cstPis || !formData.cstCofins || !formData.naturezaOperacaoId || !formData.gatilho) {
      toast.error("Preencha todos os campos obrigatórios");
      return;
    }

    if (formData.ncm.length > 8) {
      toast.error("NCM deve ter no máximo 8 dígitos");
      return;
    }

    if (formData.cstPis.length !== 2 || formData.cstCofins.length !== 2) {
      toast.error("CST deve ter 2 dígitos");
      return;
    }

    if (editingId) {
      updateMutation.mutate({ id: editingId, ...formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleSelectAll = () => {
    if (selectedIds.length === filteredPisCofins.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredPisCofins.map((item) => item.id));
    }
  };

  const handleSelectOne = (id: number) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter((selectedId) => selectedId !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  const handleDelete = () => {
    if (selectedIds.length === 0) {
      toast.error("Selecione pelo menos um registro");
      return;
    }

    if (confirm(`Deseja realmente excluir ${selectedIds.length} registro(s)?`)) {
      deleteMutation.mutate({ ids: selectedIds });
    }
  };

  const handleExport = () => {
    const data = filteredPisCofins.map((item) => ({
      "NCM": item.ncm,
      "Origem": item.origem,
      "Alíquota PIS": item.aliquotaPis,
      "Alíquota COFINS": item.aliquotaCofins,
      "CST PIS": item.cstPis || "",
      "CST COFINS": item.cstCofins || "",
      "Natureza ID": item.naturezaOperacaoId || "",
      "Natureza de Operação": item.naturezaOperacaoNome || "",
      "Gatilho": item.gatilho || "",
      "Emitente ID": item.emitenteId || "",
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "PIS_COFINS");
    XLSX.writeFile(wb, "pis_cofins.xlsx");
    toast.success("Arquivo exportado com sucesso!");
  };

  const handleImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = new Uint8Array(event.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const json: any[] = XLSX.utils.sheet_to_json(worksheet);

        const errors = validateRows(json, (row, i) => {
          const errs: ValidationError[] = [];
          const linha = i + 2;
          if (parseNumericString(row["NCM"] ?? row["ncm"]) === null) errs.push({ linha, campo: "NCM", motivo: "obrigatório, deve conter apenas dígitos" });
          if (parseIntOrNull(row["Origem"] ?? row["origem"]) === null) errs.push({ linha, campo: "Origem", motivo: "obrigatório, deve ser inteiro >= 0" });
          if (parseDecimal(row["Alíquota PIS"] ?? row["aliquotaPis"]) === null) errs.push({ linha, campo: "Alíquota PIS", motivo: "obrigatório, deve ser numérico" });
          if (parseDecimal(row["Alíquota COFINS"] ?? row["aliquotaCofins"]) === null) errs.push({ linha, campo: "Alíquota COFINS", motivo: "obrigatório, deve ser numérico" });
          return errs;
        });

        if (errors.length > 0) {
          toast.error(formatValidationErrors(errors), { duration: 8000 });
          return;
        }

        const items = json.map((row) => ({
          ncm: parseNumericString(row["NCM"] ?? row["ncm"]) as string,
          origem: parseIntOrNull(row["Origem"] ?? row["origem"]) as number,
          aliquotaPis: parseDecimal(row["Alíquota PIS"] ?? row["aliquotaPis"]) as number,
          aliquotaCofins: parseDecimal(row["Alíquota COFINS"] ?? row["aliquotaCofins"]) as number,
          cstPis: row["CST PIS"] != null ? String(row["CST PIS"]) : (row["cstPis"] != null ? String(row["cstPis"]) : ""),
          cstCofins: row["CST COFINS"] != null ? String(row["CST COFINS"]) : (row["cstCofins"] != null ? String(row["cstCofins"]) : ""),
          naturezaOperacaoId: parseIntPositive(row["Natureza ID"]) ?? undefined,
          gatilho: row["Gatilho"] ?? row["gatilho"] ?? undefined,
          emitenteId: parseIntPositive(row["Emitente ID"]) ?? undefined,
        }));

        createBulkMutation.mutate({ items });
      } catch (error) {
        toast.error("Erro ao processar arquivo");
      }
    };
    reader.readAsArrayBuffer(file);
    e.target.value = "";
  };

  const handleUpdateBulk = () => {
    if (selectedIds.length === 0) {
      toast.error("Selecione pelo menos um registro");
      return;
    }

    if (!bulkUpdateData.aliquotaPis && !bulkUpdateData.aliquotaCofins && !bulkUpdateData.cstPis && !bulkUpdateData.cstCofins && !bulkUpdateData.naturezaOperacaoId) {
      toast.error("Preencha pelo menos um campo para atualizar");
      return;
    }

    updateBulkMutation.mutate({ ids: selectedIds, ...bulkUpdateData });
  };

  const filteredPisCofins = pisCofinsList.filter((item) => {
    const search = searchTerm.toLowerCase();
    return (
      item.ncm.toLowerCase().includes(search) ||
      item.origem.toString().includes(search) ||
      item.aliquotaPis.toString().includes(search) ||
      item.aliquotaCofins.toString().includes(search) ||
      item.cstPis.toLowerCase().includes(search) ||
      item.cstCofins.toLowerCase().includes(search) ||
      (item.naturezaOperacaoNome && item.naturezaOperacaoNome.toLowerCase().includes(search))
    );
  });

  if (authLoading) {
    return <div className="flex items-center justify-center h-screen">Carregando...</div>;
  }

  return (
    <div className="container mx-auto py-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>PIS / COFINS</CardTitle>
              <CardDescription>Gerencie as alíquotas de PIS/COFINS por NCM e origem</CardDescription>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleExport}>
                <Download className="h-4 w-4 mr-2" />
                Exportar
              </Button>
              <Button variant="outline" size="sm" asChild>
                <label>
                  <Upload className="h-4 w-4 mr-2" />
                  Importar
                  <input type="file" accept=".xlsx,.xls" className="hidden" onChange={handleImport} />
                </label>
              </Button>
              {selectedIds.length > 0 && (
                <>
                  <Button variant="outline" size="sm" onClick={() => setIsUpdateBulkDialogOpen(true)}>
                    <Edit2 className="h-4 w-4 mr-2" />
                    Alterar Selecionados
                  </Button>
                  <Button variant="destructive" size="sm" onClick={handleDelete}>
                    <Trash2 className="h-4 w-4 mr-2" />
                    Excluir Selecionados
                  </Button>
                </>
              )}
              <Button size="sm" onClick={() => handleOpenDialog()}>
                <Plus className="h-4 w-4 mr-2" />
                Novo PIS/COFINS
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="mb-4">
            <Input
              placeholder="Buscar por NCM, origem, alíquota, CST ou natureza..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="border rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-muted">
                <tr>
                  <th className="px-4 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedIds.length === filteredPisCofins.length && filteredPisCofins.length > 0}
                      onChange={handleSelectAll}
                      className="h-4 w-4"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium">NCM</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Origem</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Alíquota PIS (%)</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Alíquota COFINS (%)</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">CST PIS</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">CST COFINS</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Natureza de Operação</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Emitente</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Gatilho</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredPisCofins.map((item) => (
                  <tr key={item.id} className="hover:bg-muted/50">
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(item.id)}
                        onChange={() => handleSelectOne(item.id)}
                        className="h-4 w-4"
                      />
                    </td>
                    <td className="px-4 py-3 text-sm font-medium">{item.ncm}</td>
                    <td className="px-4 py-3 text-sm">{item.origem}</td>
                    <td className="px-4 py-3 text-sm">{item.aliquotaPis}%</td>
                    <td className="px-4 py-3 text-sm">{item.aliquotaCofins}%</td>
                    <td className="px-4 py-3 text-sm">{item.cstPis}</td>
                    <td className="px-4 py-3 text-sm">{item.cstCofins}</td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      {item.naturezaOperacaoNome || "-"}
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      {item.emitenteNome || "-"}
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      {item.gatilho || "-"}
                    </td>
                    <td className="px-4 py-3">
                      <Button variant="link" size="sm" onClick={() => handleOpenDialog(item)}>
                        Editar
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Dialog de Criação/Edição */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="!w-[80vw] !h-[80vh] !max-w-none overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? "Editar" : "Novo"} PIS/COFINS</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>NCM (máx 8 dígitos) *</Label>
              <Input
                type="text"
                maxLength={8}
                value={formData.ncm}
                onChange={(e) => setFormData({ ...formData, ncm: e.target.value.replace(/\D/g, '') })}
                placeholder="12345678"
              />
            </div>

            <div>
              <Label>Origem (0-7) *</Label>
              <Select 
                value={formData.origem.toString()} 
                onValueChange={(value) => setFormData({ ...formData, origem: parseInt(value) })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">0 - Nacional</SelectItem>
                  <SelectItem value="1">1 - Estrangeira (importação direta)</SelectItem>
                  <SelectItem value="2">2 - Estrangeira (adquirida no mercado interno)</SelectItem>
                  <SelectItem value="3">3 - Nacional com conteúdo importado superior a 40%</SelectItem>
                  <SelectItem value="4">4 - Nacional (produção em conformidade com processos básicos)</SelectItem>
                  <SelectItem value="5">5 - Nacional com conteúdo importado inferior ou igual a 40%</SelectItem>
                  <SelectItem value="6">6 - Estrangeira (importação direta, sem similar nacional)</SelectItem>
                  <SelectItem value="7">7 - Estrangeira (adquirida no mercado interno, sem similar nacional)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Alíquota PIS (%) *</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={formData.aliquotaPis}
                onChange={(e) => setFormData({ ...formData, aliquotaPis: parseFloat(e.target.value) || 0 })}
              />
            </div>

            <div>
              <Label>Alíquota COFINS (%) *</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={formData.aliquotaCofins}
                onChange={(e) => setFormData({ ...formData, aliquotaCofins: parseFloat(e.target.value) || 0 })}
              />
            </div>

            <div>
              <Label>CST PIS (2 dígitos) *</Label>
              <Input
                type="text"
                maxLength={2}
                value={formData.cstPis}
                onChange={(e) => setFormData({ ...formData, cstPis: e.target.value.replace(/\D/g, '') })}
                placeholder="01"
              />
            </div>

            <div>
              <Label>CST COFINS (2 dígitos) *</Label>
              <Input
                type="text"
                maxLength={2}
                value={formData.cstCofins}
                onChange={(e) => setFormData({ ...formData, cstCofins: e.target.value.replace(/\D/g, '') })}
                placeholder="01"
              />
            </div>

            <div>
              <Label>Natureza de Operação *</Label>
              <div className="flex gap-2">
                <Input
                  value={formData.naturezaOperacaoNome}
                  readOnly
                  placeholder="Selecione uma natureza de operação"
                />
                <Button
                  type="button"
                  onClick={() => setIsNatureModalOpen(true)}
                  className="bg-orange-500 hover:bg-orange-600"
                >
                  Selecionar
                </Button>
                {formData.naturezaOperacaoId && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setFormData({ ...formData, naturezaOperacaoId: undefined, naturezaOperacaoNome: "" })}
                  >
                    Limpar
                  </Button>
                )}
              </div>
            </div>

            <div>
              <Label>Gatilho *</Label>
              <Select
                value={formData.gatilho || ""}
                onValueChange={(value) => setFormData({ ...formData, gatilho: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="consumidor_final">Venda para consumidor final</SelectItem>
                  <SelectItem value="sem_st">Venda sem ST</SelectItem>
                  <SelectItem value="com_st">Venda com ST</SelectItem>
                  <SelectItem value="alc">ALC (Área de Livre Comércio)</SelectItem>
                  <SelectItem value="zona_franca">Zona Franca de Manaus</SelectItem>
                  <SelectItem value="amazonia_ocidental">AO (Amazônia Ocidental)</SelectItem>
                  <SelectItem value="nenhum">Nenhum</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {!user?.emitenteId && (
              <div>
                <Label>Emitente *</Label>
                <div className="flex gap-2">
                  <Input
                    value={formData.emitenteNome}
                    readOnly
                    placeholder="Selecione um emitente"
                  />
                  <Button
                    type="button"
                    onClick={() => setIsEmitenteModalOpen(true)}
                    className="bg-orange-500 hover:bg-orange-600"
                  >
                    Selecionar
                  </Button>
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSubmit}>
              Salvar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog de Alteração em Massa */}
      <Dialog open={isUpdateBulkDialogOpen} onOpenChange={setIsUpdateBulkDialogOpen}>
        <DialogContent className="!w-[80vw] !h-[80vh] !max-w-none overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Alterar {selectedIds.length} Registro(s) em Massa</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Alíquota PIS (%)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                max="100"
                placeholder="Deixe em branco para não alterar"
                value={bulkUpdateData.aliquotaPis || ""}
                onChange={(e) => setBulkUpdateData({ ...bulkUpdateData, aliquotaPis: e.target.value ? parseFloat(e.target.value) : undefined })}
              />
            </div>

            <div>
              <Label>Alíquota COFINS (%)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                max="100"
                placeholder="Deixe em branco para não alterar"
                value={bulkUpdateData.aliquotaCofins || ""}
                onChange={(e) => setBulkUpdateData({ ...bulkUpdateData, aliquotaCofins: e.target.value ? parseFloat(e.target.value) : undefined })}
              />
            </div>

            <div>
              <Label>CST PIS</Label>
              <Input
                type="text"
                maxLength={2}
                placeholder="Deixe em branco para não alterar"
                value={bulkUpdateData.cstPis || ""}
                onChange={(e) => setBulkUpdateData({ ...bulkUpdateData, cstPis: e.target.value ? e.target.value.replace(/\D/g, '') : undefined })}
              />
            </div>

            <div>
              <Label>CST COFINS</Label>
              <Input
                type="text"
                maxLength={2}
                placeholder="Deixe em branco para não alterar"
                value={bulkUpdateData.cstCofins || ""}
                onChange={(e) => setBulkUpdateData({ ...bulkUpdateData, cstCofins: e.target.value ? e.target.value.replace(/\D/g, '') : undefined })}
              />
            </div>

            <div>
              <Label>Natureza de Operação</Label>
              <div className="flex gap-2">
                <Input
                  value={bulkUpdateData.naturezaOperacaoId
                    ? (operationNatures.find((n: any) => n.id === bulkUpdateData.naturezaOperacaoId) as any)?.nome || ""
                    : ""}
                  readOnly
                  placeholder="Não alterar"
                />
                <Button
                  type="button"
                  onClick={() => setIsBulkNatureModalOpen(true)}
                  className="bg-orange-500 hover:bg-orange-600"
                >
                  Selecionar
                </Button>
                {bulkUpdateData.naturezaOperacaoId && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setBulkUpdateData({ ...bulkUpdateData, naturezaOperacaoId: undefined })}
                  >
                    Limpar
                  </Button>
                )}
              </div>
            </div>

            <div>
              <Label>Gatilho</Label>
              <Select
                value={bulkUpdateData.gatilho || "unchanged"}
                onValueChange={(value) => setBulkUpdateData({ ...bulkUpdateData, gatilho: value === "unchanged" ? undefined : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Não alterar" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unchanged">Não alterar</SelectItem>
                  <SelectItem value="consumidor_final">Venda para consumidor final</SelectItem>
                  <SelectItem value="sem_st">Venda sem ST</SelectItem>
                  <SelectItem value="com_st">Venda com ST</SelectItem>
                  <SelectItem value="alc">ALC (Área de Livre Comércio)</SelectItem>
                  <SelectItem value="zona_franca">Zona Franca de Manaus</SelectItem>
                  <SelectItem value="amazonia_ocidental">AO (Amazônia Ocidental)</SelectItem>
                  <SelectItem value="nenhum">Nenhum</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <Button variant="outline" onClick={() => setIsUpdateBulkDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleUpdateBulk}>
              Atualizar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      {/* Modal de Seleção de Natureza de Operação — Formulário */}
      <Dialog open={isNatureModalOpen} onOpenChange={setIsNatureModalOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Selecionar Natureza de Operação</DialogTitle>
          </DialogHeader>
          <Input
            value={natureSearchTerm}
            onChange={(e) => setNatureSearchTerm(e.target.value)}
            placeholder="Buscar por nome, tipo, ID ou emitente..."
            className="mb-3"
          />
          <div className="max-h-[60vh] overflow-y-auto">
            <table className="w-full border-collapse">
              <thead className="sticky top-0 bg-gray-50">
                <tr>
                  <th className="border-b p-3 text-left">ID</th>
                  <th className="border-b p-3 text-left">Nome</th>
                  <th className="border-b p-3 text-left">Tipo</th>
                  <th className="border-b p-3 text-left">Emitente</th>
                  <th className="border-b p-3 text-center">Ação</th>
                </tr>
              </thead>
              <tbody>
                {(operationNatures as any[])
                  .filter((n: any) =>
                    natureSearchTerm === "" ||
                    n.nome?.toLowerCase().includes(natureSearchTerm.toLowerCase()) ||
                    n.tipo?.toLowerCase().includes(natureSearchTerm.toLowerCase()) ||
                    String(n.id).includes(natureSearchTerm) ||
                    n.emitenteNome?.toLowerCase().includes(natureSearchTerm.toLowerCase())
                  )
                  .map((n: any) => (
                    <tr key={n.id} className="hover:bg-gray-50">
                      <td className="border-b p-3 text-gray-500 font-mono text-sm">{n.id}</td>
                      <td className="border-b p-3">{n.nome}</td>
                      <td className="border-b p-3">{n.tipo}</td>
                      <td className="border-b p-3 text-sm text-gray-600">{n.emitenteNome || "-"}</td>
                      <td className="border-b p-3 text-center">
                        <Button
                          size="sm"
                          onClick={() => {
                            setFormData({ ...formData, naturezaOperacaoId: n.id, naturezaOperacaoNome: `${n.nome} — ${n.tipo}` });
                            setNatureSearchTerm("");
                            setIsNatureModalOpen(false);
                          }}
                          className="bg-orange-500 hover:bg-orange-600"
                        >
                          Selecionar
                        </Button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Seleção de Natureza de Operação — Bulk Update */}
      <Dialog open={isBulkNatureModalOpen} onOpenChange={setIsBulkNatureModalOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Selecionar Natureza de Operação</DialogTitle>
          </DialogHeader>
          <Input
            value={bulkNatureSearchTerm}
            onChange={(e) => setBulkNatureSearchTerm(e.target.value)}
            placeholder="Buscar por nome, tipo, ID ou emitente..."
            className="mb-3"
          />
          <div className="max-h-[60vh] overflow-y-auto">
            <table className="w-full border-collapse">
              <thead className="sticky top-0 bg-gray-50">
                <tr>
                  <th className="border-b p-3 text-left">ID</th>
                  <th className="border-b p-3 text-left">Nome</th>
                  <th className="border-b p-3 text-left">Tipo</th>
                  <th className="border-b p-3 text-left">Emitente</th>
                  <th className="border-b p-3 text-center">Ação</th>
                </tr>
              </thead>
              <tbody>
                {(operationNatures as any[])
                  .filter((n: any) =>
                    bulkNatureSearchTerm === "" ||
                    n.nome?.toLowerCase().includes(bulkNatureSearchTerm.toLowerCase()) ||
                    n.tipo?.toLowerCase().includes(bulkNatureSearchTerm.toLowerCase()) ||
                    String(n.id).includes(bulkNatureSearchTerm) ||
                    n.emitenteNome?.toLowerCase().includes(bulkNatureSearchTerm.toLowerCase())
                  )
                  .map((n: any) => (
                    <tr key={n.id} className="hover:bg-gray-50">
                      <td className="border-b p-3 text-gray-500 font-mono text-sm">{n.id}</td>
                      <td className="border-b p-3">{n.nome}</td>
                      <td className="border-b p-3">{n.tipo}</td>
                      <td className="border-b p-3 text-sm text-gray-600">{n.emitenteNome || "-"}</td>
                      <td className="border-b p-3 text-center">
                        <Button
                          size="sm"
                          onClick={() => {
                            setBulkUpdateData({ ...bulkUpdateData, naturezaOperacaoId: n.id });
                            setBulkNatureSearchTerm("");
                            setIsBulkNatureModalOpen(false);
                          }}
                          className="bg-orange-500 hover:bg-orange-600"
                        >
                          Selecionar
                        </Button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Seleção de Emitente */}
      <Dialog open={isEmitenteModalOpen} onOpenChange={setIsEmitenteModalOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Selecionar Emitente</DialogTitle>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto">
            <table className="w-full border-collapse">
              <thead className="sticky top-0 bg-gray-50">
                <tr>
                  <th className="border-b p-3 text-left">ID</th>
                  <th className="border-b p-3 text-left">Razão Social</th>
                  <th className="border-b p-3 text-left">CNPJ</th>
                  <th className="border-b p-3 text-center">Ação</th>
                </tr>
              </thead>
              <tbody>
                {(emitentes as any[]).map((em: any) => (
                  <tr key={em.id} className="hover:bg-gray-50">
                    <td className="border-b p-3">{em.id}</td>
                    <td className="border-b p-3">{em.razaoSocial}</td>
                    <td className="border-b p-3">{em.cnpj}</td>
                    <td className="border-b p-3 text-center">
                      <Button
                        size="sm"
                        onClick={() => {
                          setFormData({ ...formData, emitenteId: em.id, emitenteNome: em.razaoSocial });
                          setIsEmitenteModalOpen(false);
                        }}
                        className="bg-orange-500 hover:bg-orange-600"
                      >
                        Selecionar
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
